import type { OrthographicCamera, Uniform, Vector3 } from 'three';
import { Vector2 } from 'three';

import planogramConfig from 'shared/config/PlanogramConfig';
import {
  create2DCameras,
  setOrthographicCameraFrustum,
  shouldUpdateEdgeObjects,
} from 'shared/utils/CameraUtils';
import PlanogramPoint from 'shared/utils/PlanogramPoint';

import type SphereCamera from './SphereCamera';
import { debugCommand } from '../utils/debug';

const BLEED_FACTORS_AT_ZOOM = [
  { zoom: 1.5, scale: new Vector2(0.8, 0.73) },
  { zoom: 5, scale: new Vector2(0.8, 0.83) },
  { zoom: 10, scale: new Vector2(0.9, 0.94) },
  { zoom: 15, scale: new Vector2(1.0, 1.1) },
  { zoom: 20, scale: new Vector2(1.0, 1.16) },
  { zoom: 25, scale: new Vector2(1.2, 1.33) },
  { zoom: 30, scale: new Vector2(1.2, 1.43) },
];

// Since the sphere is curved, we need to render a bigger part of the canvas to avoid artifacts.
// TODO: Calculate the bleed factor based on the SphereShape
function calculateBleedFactor(zoom: number): Vector2 {
  return (
    BLEED_FACTORS_AT_ZOOM.find(bleedFactor => zoom < bleedFactor.zoom)?.scale.clone() ??
    new Vector2(1.6, 1.6)
  );
}

export default class Cameras2D {
  mainCamera: OrthographicCamera;

  zoom: number = 1;

  private textureMaterialUniforms: Record<string, Uniform>;

  private _canvasSize: Vector2;

  private cameraXAtSync?: number;

  get canvasSize() {
    return this._canvasSize;
  }

  set canvasSize(newSize: Vector2) {
    this._canvasSize = newSize;

    if (!this.mainCamera) {
      return;
    }

    setOrthographicCameraFrustum(this.mainCamera, newSize);
    this.mainCamera.updateProjectionMatrix();
    this.updateRenderedTextureMapping();
  }

  constructor(
    initialZoom: number,
    initialPanAngle: number,
    canvasSize: Vector2,
    textureMaterialUniforms: Record<string, Uniform>,
  ) {
    this.zoom = initialZoom;
    this.textureMaterialUniforms = textureMaterialUniforms;
    this.canvasSize = canvasSize;

    const initialCamera2DPosition = new Vector2((initialPanAngle / 360) * 10000, 0);
    const { mainCamera: camera2D } = create2DCameras(canvasSize, initialCamera2DPosition);

    this.mainCamera = camera2D;

    this.updateEdgeObjects();
    this.updateRenderedTextureMapping();

    {
      const weakMainCamera = new WeakRef(this.mainCamera);
      debugCommand('previewCamera', () => weakMainCamera.deref());
    }
  }

  updateFromSphereCamera(sphereCamera: SphereCamera) {
    if (!this.mainCamera) return;

    const planogramCoordinates = sphereCamera.planogramCoordinatesCurator;
    const cameraPosition = this.mainCamera.position;
    const positionChanged =
      cameraPosition.x !== planogramCoordinates.x || cameraPosition.y !== planogramCoordinates.y;

    if (positionChanged) {
      cameraPosition.x = planogramCoordinates.x;
      cameraPosition.y = planogramCoordinates.y;

      new PlanogramPoint(this.mainCamera.position).toVectorAround(
        this.mainCamera.position,
        new Vector2(),
      );

      this.updateEdgeObjects();
    }

    const zoom = Math.max(1.0, sphereCamera.maxFOV / sphereCamera.fov);
    const zoomChanged = zoom !== this.zoom;

    if (zoomChanged) {
      this.zoom = zoom;

      setOrthographicCameraFrustum(
        this.mainCamera,
        this.canvasSize,
        calculateBleedFactor(zoom).divideScalar(zoom),
      );
      this.mainCamera.updateProjectionMatrix();
    }

    if (positionChanged || zoomChanged) this.updateRenderedTextureMapping();
  }

  private edgeListeners: Array<(cameraPosition: Vector3) => void> = [];
  addEdgeListener(callback: (cameraPosition: Vector3) => void) {
    this.edgeListeners.push(callback);
  }

  private updateRenderedTextureMapping() {
    if (!this.mainCamera) return;

    const xOffset = this.mainCamera.position.x / planogramConfig.WIDTH;
    const yOffset = this.mainCamera.position.y / planogramConfig.HEIGHT;

    const renderedHeight = this.mainCamera.top - this.mainCamera.bottom;
    const renderedWidth = this.mainCamera.right - this.mainCamera.left;

    this.textureMaterialUniforms.uvOffset.value.x = xOffset;
    this.textureMaterialUniforms.uvOffset.value.y = yOffset;

    this.textureMaterialUniforms.uvScale.value.y = renderedHeight / planogramConfig.HEIGHT;
    this.textureMaterialUniforms.uvScale.value.x = renderedWidth / planogramConfig.WIDTH;
  }

  private updateEdgeObjects() {
    if (!this.mainCamera) return;
    const cameraPosition = this.mainCamera.position;

    if (shouldUpdateEdgeObjects(cameraPosition.x, this.cameraXAtSync)) {
      this.edgeListeners.forEach(it => it(cameraPosition));
      this.cameraXAtSync = cameraPosition.x;
    }
  }
}
